// SignInScreen.jsx — PR ② (SSO). Google Identity Services sign-in.
//
// Two modes: full-page gate (auth demanded — post-flip or session revoked) and
// overlay (voluntary sign-in pre-flip, from the TopBar). The GIS script is
// injected on demand — public/index.html stays untouched. Error states follow
// the §6 table verbatim; not_provisioned shows the attempted email (decoded
// from the Google ID token, display-only — the backend does the verifying).

import React from 'react';
import { useTheme } from './theme';
import { GOOGLE_CLIENT_ID, signInWithGoogleCredential, decodeJwtPayload } from './auth';

let gisPromise = null;
function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { gisPromise = null; reject(new Error('gis_load_failed')); };
    document.head.appendChild(s);
  });
  return gisPromise;
}

// §6 table → user-facing copy. Codes not listed fall through to a generic line.
function errorCopy(code, email) {
  switch (code) {
    case 'not_provisioned':
      return { title: 'Not provisioned', body: `Ask your admin to add ${email || 'your email'} to Mono. Sign-up is by provisioning only.` };
    case 'email_conflict':
      return { title: 'Account conflict', body: 'This email is bound to a different Google account. Contact support to resolve it.' };
    case 'inactive':
      return { title: 'Access revoked', body: 'Your user or organisation has been deactivated. Contact your admin.' };
    case 'sso_unconfigured':
    case 'db_unconfigured':
      return { title: 'Maintenance', body: 'Sign-in is temporarily unavailable while the backend is being configured. Try again shortly.' };
    case 'invalid_refresh':
    case 'reuse_detected':
      return { title: 'Session ended', body: 'Your session is no longer valid. Sign in again to continue.' };
    case 'gis_load_failed':
      return { title: 'Could not load Google sign-in', body: 'Check the network (or an ad-blocker) and reload.' };
    default:
      return code ? { title: 'Sign-in failed', body: `The backend rejected the sign-in (${code}).` } : null;
  }
}

export default function SignInScreen({ overlay = false, onClose, initialErrorCode = null }) {
  const { P, FN, MO, DISPLAY } = useTheme();
  const btnRef = React.useRef(null);
  const [err, setErr] = React.useState(initialErrorCode ? { code: initialErrorCode, email: null } : null);
  const [busy, setBusy] = React.useState(false);
  const configured = !!GOOGLE_CLIENT_ID;

  React.useEffect(() => {
    if (!configured) return undefined;
    let cancelled = false;
    loadGis().then(() => {
      if (cancelled || !window.google?.accounts?.id || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          setBusy(true); setErr(null);
          try {
            await signInWithGoogleCredential(credential);   // state flows via auth.js emit
            if (onClose) onClose();
          } catch (e) {
            const email = decodeJwtPayload(credential)?.email || null;
            setErr({ code: e.code || `http_${e.status || '?'}`, email });
          } finally {
            setBusy(false);
          }
        },
      });
      window.google.accounts.id.renderButton(btnRef.current, { theme: 'outline', size: 'large' });
    }).catch(() => { if (!cancelled) setErr({ code: 'gis_load_failed', email: null }); });
    return () => { cancelled = true; };
  }, [configured, onClose]);

  const copy = err ? errorCopy(err.code, err.email) : null;

  const card = (
    <div onClick={(e) => e.stopPropagation()} style={{
      width: '100%', maxWidth: 400, background: P.sheet, border: `0.5px solid ${P.rule}`,
      borderRadius: 14, padding: '28px 28px 24px', boxShadow: '0 24px 60px rgba(20,16,12,.22)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center',
    }}>
      <svg width="30" height="30" viewBox="0 0 22 22" fill="none" aria-hidden>
        <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3" stroke={P.ink} strokeWidth="1.2" />
        <rect x="6" y="6" width="10" height="10" rx="1.5" fill={P.accent} />
      </svg>
      <div>
        <div style={{ fontFamily: DISPLAY, fontSize: 26, color: P.ink, lineHeight: 1.1 }}>Sign in to Mono</div>
        <div style={{ fontFamily: FN, fontSize: 12, color: P.muted, marginTop: 6, lineHeight: 1.5 }}>
          Use the Google account your admin provisioned.
        </div>
      </div>

      {!configured ? (
        <div style={{
          fontFamily: MO, fontSize: 11, color: P.warn, background: P.warnSoft,
          border: `0.5px solid ${P.warnLine}`, borderRadius: 8, padding: '10px 12px', lineHeight: 1.5,
        }}>
          REACT_APP_GOOGLE_CLIENT_ID is not set — add it to the build environment and rebuild.
        </div>
      ) : (
        <div ref={btnRef} style={{ minHeight: 44, opacity: busy ? 0.6 : 1 }} />
      )}

      {busy && <div style={{ fontFamily: MO, fontSize: 10.5, color: P.dim }}>Signing in…</div>}

      {copy && (
        <div style={{
          fontFamily: FN, fontSize: 12, color: P.ink, background: P.dangerSoft,
          border: `0.5px solid ${P.dangerLine}`, borderRadius: 8, padding: '10px 12px',
          lineHeight: 1.5, textAlign: 'left', width: '100%', boxSizing: 'border-box',
        }}>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 0.8, textTransform: 'uppercase', color: P.danger, marginBottom: 3 }}>{copy.title}</div>
          {copy.body}
        </div>
      )}

      <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, lineHeight: 1.5 }}>
        Access is provisioned by your organisation — there is no open sign-up.
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 240, background: 'rgba(20,16,12,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
        {card}
      </div>
    );
  }
  return (
    <div style={{ minHeight: '100vh', background: P.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      {card}
    </div>
  );
}
